import { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';
import { CustomButton, CustomInput, CustomSelect, Picker, Text } from '../../../shared/uiComponents';
import SearchSelect, { SelectDataProperties } from '../../../shared/uiComponents/Dropdown/searchSelect';
import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { anchorWallClockTime, parseWallClockTime } from '../../../shared/Helpers/timezones';
import { useCompareDates } from '../../../shared/Helpers/hooks';
import { TimeInputsWrapper } from '../../Reports/helpers';
import { DispatchProperties, useSelector } from '../../../redux/store';
import { AdminHourServiceCode, AdminHours } from '../../../redux/API/ClientAPIHelpers/adminHours';
import { DiscretionaryEventDataProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { UserSessionAllocationMonthlyDetailsProperties } from '../../../redux/API/userAPIProperties';
import { getClients } from '../../../redux/State/clientSlice/clientSlice';
import { createDiscretionaryHours, updateDiscretionaryHours } from '../../../redux/State/clientSlice/calendarSlice';
import { useResetCalendarData } from '../Helpers/hooks';

const AdminHoursForm = ({
	existing,
	onClose,
}: {
	existing?: DiscretionaryEventDataProperties;
	onClose: () => void;
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();
	const actorId = useSelector((state) => state.account.user.id);
	const day = useSelector((state) => state.calendarHelper.selectedDay);
	const ownerId = existing?.user.id || actorId;
	const [date, setDate] = useState<Dayjs | null>(
		existing ? parseWallClockTime(existing.date) : dayjs().year(day.year).month(day.month).date(day.day),
	);
	const [start, setStart] = useState<Dayjs | null>(existing ? parseWallClockTime(existing.startTime) : null);
	const [end, setEnd] = useState<Dayjs | null>(existing ? parseWallClockTime(existing.endTime) : null);
	const [description, setDescription] = useState(existing?.description || '');
	const [notes, setNotes] = useState(existing?.notes || '');
	const [clientId, setClientId] = useState(existing?.clientId || '');
	const [clientSearch, setClientSearch] = useState(existing?.clientFullName || '');
	const [clients, setClients] = useState<SelectDataProperties[]>([]);
	const [pageSize, setPageSize] = useState(10);
	const [hasMore, setHasMore] = useState(false);
	const [codeId, setCodeId] = useState(existing?.serviceCodeId || '');
	const [codes, setCodes] = useState<AdminHourServiceCode[]>([]);
	const [usage, setUsage] = useState<UserSessionAllocationMonthlyDetailsProperties | null>(null);
	const [loadingUsage, setLoadingUsage] = useState(true);
	const [saving, setSaving] = useState(false);
	const { hasError, message } = useCompareDates(start, end);

	useEffect(() => {
		AdminHours.getServiceCodes().then(setCodes).catch(errorNormalizer);
	}, []);
	useEffect(() => {
		let current = true;
		setUsage(null);
		setLoadingUsage(Boolean(date && ownerId));
		if (date && ownerId)
			AdminHours.getUsage(ownerId, dateNormalizer(date))
				.then((response) => {
					if (current) setUsage(response);
				})
				.catch(errorNormalizer)
				.finally(() => {
					if (current) setLoadingUsage(false);
				});

		return () => {
			current = false;
		};
	}, [date, ownerId]);
	useEffect(() => {
		let current = true;
		const timer = setTimeout(() => {
			dispatch(getClients({ page: '1', pageSize: `${pageSize}`, searchString: clientSearch }))
				.then(unwrapResult)
				.then((response) => {
					if (!current) return;
					setClients((response.query || []).map((client) => ({ id: client.id, label: client.fullName || '' })));
					setHasMore(Boolean(response.hasNextPage));
				})
				.catch(errorNormalizer);
		}, 500);

		return () => {
			current = false;
			clearTimeout(timer);
		};
	}, [dispatch, clientSearch, pageSize]);

	const save = () => {
		if (!date || !start || !end || !description.trim() || !usage) return;
		const dateString = dateNormalizer(date);
		const data = {
			date: dateString,
			startTime: `${dateString}T${start.format('HH:mm')}`,
			endTime: `${dateString}T${end.format('HH:mm')}`,
			description: description.trim(),
			notes: notes || undefined,
			clientId: clientId || null,
			serviceCodeId: codeId || null,
		};
		setSaving(true);
		const action = existing
			? dispatch(updateDiscretionaryHours({ id: existing.id, data }))
			: dispatch(createDiscretionaryHours(data));
		action
			.then(unwrapResult)
			.then(() => {
				toast('Admin Hours saved');
				resetData();
				onClose();
			})
			.catch(errorNormalizer)
			.finally(() => setSaving(false));
	};

	return (
		<>
			<Picker.CustomDate label="Date" value={date} onChange={setDate} className="marginBottom16" />
			<TimeInputsWrapper>
				<Picker.CustomTime
					label="Start time (Eastern)"
					value={start}
					onChange={(value) => {
						const anchored = date ? anchorWallClockTime(`${dateNormalizer(date)}T00:00:00`, value) : null;
						setStart(anchored);
						if (!existing && anchored) setEnd(anchored.add(1, 'hour'));
					}}
				/>
				<Picker.CustomTime
					label="End time (Eastern)"
					value={end}
					onChange={(value) => setEnd(date ? anchorWallClockTime(`${dateNormalizer(date)}T00:00:00`, value) : null)}
					error={hasError}
					errorMessage={message}
				/>
			</TimeInputsWrapper>
			<CustomInput
				label="Service description"
				value={description}
				onChange={(event) => setDescription(event.target.value)}
				className="marginBottom16"
			/>
			<SearchSelect
				label="Client (optional)"
				className="marginBottom16"
				searchString={clientSearch}
				setSearchString={(value) => {
					setClientSearch(value);
					setClientId('');
					setPageSize(10);
				}}
				data={clients}
				setValue={setClientId}
				hasMore={hasMore}
				setSize={setPageSize}
				clearData={() => {
					setClientId('');
					setClientSearch('');
				}}
			/>
			<CustomSelect
				label="Service code (optional)"
				value={codeId}
				setValue={(event) => setCodeId(event.target.value)}
				data={[
					{ id: '', label: 'None' },
					...(codeId && !codes.some((code) => code.id === codeId)
						? [{ id: codeId, label: existing?.serviceCodeName || 'Existing code (inactive)' }]
						: []),
					...codes.map((code) => ({ id: code.id, label: `${code.code} - ${code.name}` })),
				]}
				className="marginBottom16"
			/>
			<CustomInput
				label="Notes (optional)"
				value={notes}
				onChange={(event) => setNotes(event.target.value)}
				multiline
				className="marginBottom16"
			/>
			<Text
				type="psm"
				className="marginBottom16"
				title={loadingUsage
					? 'Loading allocation…'
					: usage
						? `${usage.usedHoursThisMonth.toFixed(2)} used; ${usage.remainingHoursThisMonth.toFixed(2)} remaining of ${usage.allocatedHoursPerMonth} hours this month.`
						: 'No active Admin Hours allocation.'}
			/>
			<Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px" flexWrap="wrap" alignSelf="stretch">
				<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} disabled={saving} />
				<CustomButton
					title={existing ? 'Save changes' : 'Log hours'}
					onClick={save}
					loading={saving}
					disabled={loadingUsage || !usage || !date || !start || !end || !description.trim() || hasError}
				/>
			</Stack>
		</>
	);
};

export default AdminHoursForm;
